import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useAppendBookingNote } from "@/hooks/useReservations";

interface AddBookingNoteModalProps {
  bookingId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const AddBookingNoteModal: React.FC<AddBookingNoteModalProps> = ({
  bookingId,
  open,
  onOpenChange,
}) => {
  const { t } = useTranslation();
  const [content, setContent] = useState("");
  const appendNote = useAppendBookingNote(bookingId);

  useEffect(() => {
    if (!open) {
      setContent("");
    }
  }, [open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = content.trim();
    if (!trimmed) {
      toast.error(t("reservation.notesSection.noteRequired"));
      return;
    }
    try {
      await appendNote.mutateAsync(trimmed);
      toast.success(t("reservation.notesSection.noteAdded"));
      onOpenChange(false);
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : t("reservation.notesSection.noteAddFailed");
      toast.error(message);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>
              {t("reservation.notesSection.modalTitle")}
            </DialogTitle>
          </DialogHeader>
          <div className="grid gap-2 py-2">
            <Label htmlFor="booking-note-content">
              {t("reservation.notesSection.contentLabel")}
            </Label>
            <Textarea
              id="booking-note-content"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder={t("reservation.notesSection.contentPlaceholder")}
              rows={5}
              className="resize-y min-h-[120px]"
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={appendNote.isPending}
            >
              {t("reservation.notesSection.cancel")}
            </Button>
            <Button type="submit" disabled={appendNote.isPending}>
              {t("reservation.notesSection.submit")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
